/**
 * Types for the reports slice.
 *
 * The strongly-typed rows come straight from the generated OpenAPI schema. A
 * few endpoints expose `DictField`/`ListField` payloads that drf-spectacular
 * can only type as `{ [key: string]: unknown }[]` — for those we declare the
 * precise shape here (the backend serializers guarantee it).
 */
import type { components } from "@/lib/api/schema.gen";

export type EnterpriseReportRow = components["schemas"]["EnterpriseReportList"];
export type EnterpriseReportDetail =
  components["schemas"]["EnterpriseReportDetail"];
export type EnterpriseProgressRow =
  components["schemas"]["EnterpriseReportProgress"];
export type AssessmentSummary = components["schemas"]["AssessmentSummary"];
export type AssessmentDataRow = components["schemas"]["AssessmentDataRow"];

/** The seven progress-step keys, in order. */
export const PROGRESS_STEPS = [
  "step_0",
  "step_1",
  "step_2",
  "step_3",
  "step_4",
  "step_5",
  "step_6",
] as const;
export type ProgressStep = (typeof PROGRESS_STEPS)[number];

export interface IdName {
  id: string;
  name: string;
}

export interface ValueLabel {
  value: string;
  label: string;
}

/**
 * A sector category with its sub-sectors. Declared here (rather than reused
 * from the generated schema) because the backend's `get_sectors`
 * SerializerMethodField has no type hint, so drf-spectacular types the nested
 * list as a plain `string`.
 */
export interface SectorCategoryOption {
  id: string;
  name: string;
  sectors: IdName[];
}

/** Precise shape of the /filters/options/ response (loosely typed in schema). */
export interface FilterOptions {
  sector_categories: SectorCategoryOption[];
  regions: string[];
  mrcs: string[];
  /** Groups the viewer may break a report down by (all of them for an admin). */
  partner_groups: IdName[];
  issues: IdName[];
  answer_types: ValueLabel[];
  enterprise_types: ValueLabel[];
}

/** The report whose filters a facet request describes. */
export type ReportPage = "list" | "progress" | "assessment";

/** The fields whose options follow the other filters. */
export type FacetField =
  | "sector_categories"
  | "sectors"
  | "region"
  | "mrc"
  | "city"
  | "province"
  | "issues"
  | "goals"
  | "actions"
  | "answer_types";

/**
 * The /filters/facets/ response: for each faceted field of the page, the
 * values the other filters can still find — the current selection included,
 * so it can be cleared. `id` is what the filter submits (a region's own name,
 * an answer type's value).
 */
export type ReportFacets = Partial<Record<FacetField, IdName[]>>;

/** Filters of the enterprise list and progress views. */
export interface EnterpriseFilterState {
  q?: string;
  partner_group?: string;
  mrc?: string;
  /** Months (YYYY-MM) — the progress view's sign-up range, from the start
   * month up to, not including, the end month, as on the Django page. */
  start_date?: string;
  end_date?: string;
  sector_categories?: string;
  sectors?: string[];
  issues?: string[];
  goals?: string[];
  actions?: string[];
  answer_types?: string;
}

/** Filters for the customizable assessment. */
export interface AssessmentFilterState {
  partner_group?: string;
  city?: string;
  region?: string;
  mrc?: string;
  province?: string;
  sector_categories?: string;
  sectors?: string[];
  /** Months (YYYY-MM), end month excluded as on the Django « Bilan »: the
   * sign-up range, which also limits the answers counted. */
  date_from?: string;
  date_to?: string;
  /** Which nodes of the thematic table to show and count, as on the Django
   * « Bilan » — they do not narrow the enterprises counted. */
  issues?: string[];
  goals?: string[];
  actions?: string[];
}

export type AssessmentLevel =
  | "thematic"
  | "issue"
  | "goal"
  | "action"
  | "enterprise";
